import { useState } from 'react';
import { TrendingUp, Trophy, Target, Calendar, BarChart3, LineChart, PieChart, Award } from 'lucide-react';

interface StatItem {
  label: string;
  value: string | number;
  change?: number;
  unit?: string;
}

interface CompetitionResult {
  id: string;
  event: string;
  date: string;
  position: number;
  location: string;
  category: string;
}

interface StatsData {
  sport: string;
  season: string;
  personalBests: StatItem[];
  currentSeason: StatItem[];
  careerStats: StatItem[];
  competitions: CompetitionResult[];
  ranking: {
    national: number;
    international: number;
    category: string;
  };
}

interface StatsSectionProps {
  data: StatsData;
}

const StatsSection = ({ data }: StatsSectionProps) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'competitions' | 'rankings'>('overview');

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: <BarChart3 size={18} /> },
    { id: 'performance', label: 'Rendimiento', icon: <LineChart size={18} /> },
    { id: 'competitions', label: 'Competencias', icon: <Trophy size={18} /> },
    { id: 'rankings', label: 'Rankings', icon: <Award size={18} /> }
  ];

  const StatCard = ({ stat, showTrend = false }: { stat: StatItem; showTrend?: boolean }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</span>
        {showTrend && typeof stat.change === 'number' && (
          <div className={`flex items-center text-xs ${
            stat.change > 0 ? 'text-green-500' : stat.change < 0 ? 'text-red-500' : 'text-gray-500'
          }`}>
            <TrendingUp size={12} className="mr-1" />
            {stat.change > 0 ? '+' : ''}{stat.change}%
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">
        {stat.value}
        {stat.unit && <span className="text-sm text-gray-500 ml-1">{stat.unit}</span>}
      </div>
    </div>
  );

  const PerformanceChart = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Progreso de Temporada</h3>
        <select className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option>2024</option>
          <option>2023</option>
          <option>2022</option>
        </select>
      </div>
      <div className="h-64 flex items-end justify-between space-x-2">
        {[65, 72, 68, 75, 82, 78, 85, 88, 92, 89, 94, 96].map((height, index) => (
          <div key={index} className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-green-500 rounded-t-sm transition-all duration-300 hover:bg-green-600"
              style={{ height: `${height}%` }}
            />
            <span className="text-xs text-gray-500 mt-2">
              {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][index]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const CompetitionCard = ({ competition }: { competition: CompetitionResult }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white">{competition.event}</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">{competition.location}</p>
        </div>
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
          competition.position === 1 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
          competition.position <= 3 ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
        }`}>
          {competition.position === 1 ? '🥇' : competition.position === 2 ? '🥈' : competition.position === 3 ? '🥉' : `#${competition.position}`}
        </div>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500 dark:text-gray-400">{competition.category}</span>
        <span className="text-gray-500 dark:text-gray-400">{new Date(competition.date).toLocaleDateString()}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Estadísticas Deportivas</h2>
          <p className="text-gray-600 dark:text-gray-400">{data.sport} • Temporada {data.season}</p>
        </div>
        <button className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200">
          <Target size={18} className="mr-2" />
          Actualizar Stats
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-600 dark:text-green-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <>
            {/* Personal Bests */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Récords Personales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {data.personalBests.map((stat, index) => (
                  <StatCard key={index} stat={stat} />
                ))}
              </div>
            </div>

            {/* Current Season */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Temporada Actual</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {data.currentSeason.map((stat, index) => (
                  <StatCard key={index} stat={stat} showTrend />
                ))}
              </div>
            </div>

            {/* Career Overview */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resumen de Carrera</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {data.careerStats.map((stat, index) => (
                  <StatCard key={index} stat={stat} />
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'performance' && (
          <>
            <PerformanceChart />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Performance Metrics */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Métricas de Rendimiento</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Consistencia</span>
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">85%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Mejora</span>
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '72%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">72%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Competitividad</span>
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                        <div className="bg-purple-500 h-2 rounded-full" style={{ width: '91%' }}></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">91%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Goals Progress */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Objetivos 2024</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Clasificar a Nacionales</span>
                      <span className="text-sm font-medium text-green-600">Completado</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Mejorar marca personal</span>
                      <span className="text-sm font-medium text-blue-600">78%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Top 3 en Regionales</span>
                      <span className="text-sm font-medium text-yellow-600">45%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '45%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'competitions' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Historial de Competencias</h3>
              <div className="flex items-center space-x-2">
                <select className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option>Todas las categorías</option>
                  <option>Nacional</option>
                  <option>Internacional</option>
                  <option>Regional</option>
                </select>
                <select className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option>2024</option>
                  <option>2023</option>
                  <option>2022</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.competitions.map((competition) => (
                <CompetitionCard key={competition.id} competition={competition} />
              ))}
            </div>

            {/* Competition Summary */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resumen de Competencias 2024</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">12</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Competencias</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-500">3</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Medallas de Oro</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-400">2</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Medallas de Plata</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-500">1</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Medallas de Bronce</div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'rankings' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Current Rankings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rankings Actuales</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Nacional</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{data.ranking.category}</div>
                    </div>
                    <div className="text-2xl font-bold text-green-500">#{data.ranking.national}</div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Internacional</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{data.ranking.category}</div>
                    </div>
                    <div className="text-2xl font-bold text-blue-500">#{data.ranking.international}</div>
                  </div>
                </div>
              </div>

              {/* Ranking History */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Evolución del Ranking</h3>
                <div className="h-32 flex items-end justify-between space-x-1">
                  {[15, 12, 8, 6, 4, 3, 2, 3, 2, 1, 2, 1].map((position, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t-sm"
                        style={{ height: `${(16 - position) * 6}px` }}
                      />
                      <span className="text-xs text-gray-500 mt-1">#{position}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>Ene</span>
                  <span>Dic</span>
                </div>
              </div>
            </div>

            {/* Ranking Comparison */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Comparación con Competidoras</h3>
              <div className="space-y-3">
                {[
                  { name: 'Ana García', position: 1, points: 2450, trend: 'up' },
                  { name: 'María Rodriguez', position: 2, points: 2380, trend: 'same', current: true },
                  { name: 'Sofia López', position: 3, points: 2320, trend: 'down' },
                  { name: 'Carmen Ruiz', position: 4, points: 2280, trend: 'up' },
                  { name: 'Laura Martín', position: 5, points: 2250, trend: 'up' }
                ].map((athlete, index) => (
                  <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                    athlete.current ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-700'
                  }`}>
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                        athlete.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                        athlete.position === 2 ? 'bg-gray-100 text-gray-800' :
                        athlete.position === 3 ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {athlete.position}
                      </div>
                      <div>
                        <div className={`font-medium ${athlete.current ? 'text-green-900 dark:text-green-100' : 'text-gray-900 dark:text-white'}`}>
                          {athlete.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{athlete.points} puntos</div>
                      </div>
                    </div>
                    <div className={`text-sm ${
                      athlete.trend === 'up' ? 'text-green-500' :
                      athlete.trend === 'down' ? 'text-red-500' :
                      'text-gray-500'
                    }`}>
                      {athlete.trend === 'up' ? '↗' : athlete.trend === 'down' ? '↘' : '→'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StatsSection;